// Ground coming back.
//
// It exists for the board where no camp will fit: a map with nowhere to put a
// front would leave the run stalled. Regrowth is the other thing to offer.
//
// A seed goes at the edge **nearest** your army, and each turn every seed pushes its cardinal
// neighbours onto the board, handing each its own counter minus one. So the
// growth radiates outward and runs down, reaching exactly as far as the number
// it started with, and it arrives *beside you* rather than across the map.
//
// Three things it does not need, because they already exist:
//
//   - **The rise.** `BoardTiles.setCells` raises anything new from under the
//     board, staggered by distance from ground that was already there. New
//     squares come up out of the dark for free.
//   - **A budget.** The counter *is* the budget: it is the reach and the
//     number of turns at once, because one step spends one.
//   - **A per-turn cap.** A cardinal push from a running frontier is already
//     bounded by the frontier's own size.

import { type Direction, ROOK_DIRS } from "./board";
import { hasCell, type Position, setCells, setPromotionUses } from "./position";
import { growthAt, growthSquares, type IndustryState, setGrowth } from "./industry/state";
import { RULES } from "./rules";

/**
 * Where regrowth starts: the edge square nearest your army.
 *
 * Ground arrives as close as it can, because the point of it is somewhere to
 * stand. The same reading of "edge" — a square missing at least one of its
 * neighbours, which on a ragged board is the real frontier rather than the
 * bounding box.
 *
 * Ties break by square, like every other ordering here.
 */
export function growthSeed(position: Position, side: number): number {
  const mine: number[] = [];
  for (const [sq, piece] of position.board) {
    if ((piece >> 3) === side) mine.push(sq);
  }

  let best = -1;
  let bestAway = Infinity;
  for (const sq of position.cellList) {
    let open = false;
    for (const dir of ROOK_DIRS) {
      if (!hasCell(position, sq + dir)) {
        open = true;
        break;
      }
    }
    if (!open) continue;

    let away = Infinity;
    for (const at of mine) {
      const df = Math.abs((sq & 1023) - (at & 1023));
      const dr = Math.abs((sq >> 10) - (at >> 10));
      away = Math.min(away, Math.max(df, dr));
    }
    if (away < bestAway) {
      bestAway = away;
      best = sq;
    }
  }
  return best;
}

/** What one turn of growth laid, so it can be reported and drawn. */
export interface GrowthStep {
  /** Squares that arrived this turn. */
  grown: number[];
  /** Of those, the ones that came up as promotion squares. */
  promoted: number[];
}

/**
 * One turn of the ground coming back.
 *
 * Every seed pushes its cardinal neighbours onto the board and hands each its
 * own counter minus one; the seed is then spent. A counter of one lays its
 * ring and stops, which is what makes the reach exactly the number it started
 * with.
 *
 * **The frontier is read before anything is added**: a square laid this turn must not push in the same turn, or a
 * counter of six lays a six-square *spike* in one go instead of six rings.
 *
 * `roll` is the seeded stream a promotion square is drawn off. Optional, and
 * without it the ground still arrives — the prize is the part a caller may not
 * want in a test, never the ground.
 */
export function runGrowth(
  position: Position,
  industry: IndustryState,
  roll?: (square: number) => () => number
): GrowthStep {
  const seeds = growthSquares(industry);
  if (seeds.length === 0) return { grown: [], promoted: [] };

  const grown: number[] = [];
  const promoted: number[] = [];
  const added = new Set<number>();

  for (const sq of seeds) {
    const left = growthAt(industry, sq);
    // Spent whether or not it had anywhere to push: a seed hemmed in by ground
    // that already exists is done, and leaving it set would have it try again
    // every turn for the rest of the round.
    setGrowth(industry, sq, 0);
    if (left <= 1) continue;

    for (const dir of ROOK_DIRS as readonly Direction[]) {
      const at = sq + dir;
      if (hasCell(position, at) || added.has(at)) continue;
      added.add(at);
      grown.push(at);
      // **The lower counter goes on the *new* square**, which is what makes
      // this radiate rather than pulse: the ring that has just arrived is the
      // only thing that pushes next turn.
      setGrowth(industry, at, left - 1);
    }
  }

  if (grown.length === 0) return { grown, promoted };

  setCells(position, [...position.cellList, ...grown]);

  // The prize, after the ground exists — `setPromotionUses` is about a square
  // on the board, and a marker on a cell that is not there yet is an orphan.
  if (roll) {
    for (const sq of grown) {
      if (roll(sq)() >= RULES.growPromotion) continue;
      setPromotionUses(position, sq, 1);
      promoted.push(sq);
    }
  }
  return { grown, promoted };
}
